import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Shield, 
    Zap, 
    TrendingUp, 
    Activity, 
    Plus, 
    ChevronLeft,
    Clock,
    DollarSign,
    Target,
    Briefcase
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { 
    verifyAdminAccess, 
    subscribeToWadeActions, 
    logBigBet 
} from '../../services/adminService';

interface WadeAction {
    id: string;
    timestamp: any;
    message: string;
    status: 'success' | 'working' | 'error';
    category: string;
}

export default function AppAdminPortal() {
    const navigate = useNavigate();
    const { user, isLoading: authLoading } = useAuth();
    const [isAdmin, setIsAdmin] = useState(false);
    const [actions, setActions] = useState<WadeAction[]>([]);
    const [loading, setLoading] = useState(true);

    // Big Bet Scout Form State
    const [betTitle, setBetTitle] = useState('');
    const [betCategory, setBetCategory] = useState<'forex' | 'real_estate' | 'options'>('forex');
    const [betDetail, setBetDetail] = useState('');
    const [isLogging, setIsLogging] = useState(false);

    useEffect(() => {
        if (authLoading) return;

        if (user) {
            const hasAccess = verifyAdminAccess(user.id) || user.email === "justin@way2will.ai";
            setIsAdmin(hasAccess);
            
            if (!hasAccess) {
                console.warn("[Admin] Unauthorized access attempt, redirecting...");
                navigate('/app');
                return;
            }

            // Subscribe to Wade Actions
            const unsubscribe = subscribeToWadeActions((newActions) => {
                const sorted = newActions.sort((a, b) => 
                    (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0)
                );
                setActions(sorted);
                setLoading(false);
            });

            return () => unsubscribe();
        }
    }, [user, authLoading, navigate]);

    const handleLogBigBet = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!betTitle || !betDetail) return;

        setIsLogging(true);
        try {
            await logBigBet({
                title: betTitle,
                category: betCategory,
                detail: betDetail
            });
            setBetTitle('');
            setBetDetail('');
            alert('Big Bet Logged to Board.');
        } catch (error) {
            console.error("Error logging big bet:", error);
            alert('Failed to log big bet.');
        } finally {
            setIsLogging(false);
        }
    };

    if (!isAdmin) return null;

    return (
        <div className="min-h-screen bg-[#F8FAFB] pb-12">
            {/* Header */}
            <div className="bg-white px-4 py-4 border-b border-[#E5EBF0] sticky top-0 z-10">
                <div className="flex items-center gap-4">
                    <button 
                        onClick={() => navigate('/app/profile')}
                        className="p-2 hover:bg-[#F5F8FA] rounded-full"
                    >
                        <ChevronLeft className="w-6 h-6 text-[#1A2832]" />
                    </button>
                    <div>
                        <h1 className="text-xl font-bold text-[#1A2832] flex items-center gap-2">
                            <Shield className="w-5 h-5 text-[#0D5F6B]" />
                            Admin Portal
                        </h1>
                        <p className="text-xs text-[#7B8C9B]">Wade Executive Interface</p>
                    </div>
                </div>
            </div>

            <div className="px-4 py-6 max-w-2xl mx-auto space-y-8">
                
                {/* Section 1: Wade Action Log */}
                <section>
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-bold text-[#1A2832] flex items-center gap-2">
                            <Activity className="w-5 h-5 text-[#0D5F6B]" />
                            Wade Actions
                        </h2>
                        <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-[#D5F2EA] text-[#3DA58A] uppercase">
                            Live Feed
                        </span>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-[#E5EBF0] overflow-hidden">
                        {loading ? (
                            <div className="p-8 text-center">
                                <div className="w-8 h-8 border-2 border-[#0D5F6B] border-t-transparent rounded-full animate-spin mx-auto mb-2"></div>
                                <p className="text-sm text-[#7B8C9B]">Synchronizing with Wade...</p>
                            </div>
                        ) : actions.length === 0 ? (
                            <div className="p-8 text-center text-[#7B8C9B] text-sm">
                                No actions logged in this cycle.
                            </div>
                        ) : (
                            <div className="divide-y divide-[#E5EBF0]">
                                {actions.map((action) => (
                                    <div key={action.id} className="p-4 hover:bg-[#F8FAFB] transition-colors">
                                        <div className="flex items-start gap-3">
                                            <div className={`mt-1 w-2 h-2 rounded-full shrink-0 ${
                                                action.status === 'success' ? 'bg-green-500' : 
                                                action.status === 'working' ? 'bg-blue-500 animate-pulse' : 
                                                'bg-red-500'
                                            }`} />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-[#1A2832] break-words">
                                                    {action.message}
                                                </p>
                                                <div className="flex items-center gap-3 mt-1">
                                                    <span className="text-[10px] text-[#7B8C9B] flex items-center gap-1">
                                                        <Clock className="w-3 h-3" />
                                                        {action.timestamp?.toDate ? action.timestamp.toDate().toLocaleTimeString() : 'Recent'}
                                                    </span>
                                                    <span className="text-[10px] font-bold text-[#0D5F6B] uppercase tracking-wider">
                                                        {action.category}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </section>

                {/* Section 2: Big Bet Scout */}
                <section>
                    <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="w-5 h-5 text-[#0D5F6B]" />
                        <h2 className="text-lg font-bold text-[#1A2832]">Big Bet Scout</h2>
                    </div>

                    <div className="bg-white rounded-2xl shadow-sm border border-[#E5EBF0] p-6">
                        <form onSubmit={handleLogBigBet} className="space-y-4">
                            <div>
                                <label className="block text-xs font-bold text-[#7B8C9B] uppercase mb-1">Bet Title</label>
                                <input 
                                    type="text" 
                                    value={betTitle}
                                    onChange={(e) => setBetTitle(e.target.value)}
                                    placeholder="e.g. High-Yield Multi-Family in Austin"
                                    className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] focus:outline-none focus:border-[#0D5F6B] text-[#1A2832]"
                                    required
                                />
                            </div>

                            <div className="grid grid-cols-3 gap-2">
                                {(['forex', 'real_estate', 'options'] as const).map((cat) => (
                                    <button
                                        key={cat}
                                        type="button"
                                        onClick={() => setBetCategory(cat)}
                                        className={`py-2 rounded-lg text-xs font-bold transition-all ${
                                            betCategory === cat 
                                            ? 'bg-[#0D5F6B] text-white' 
                                            : 'bg-[#F5F8FA] text-[#7B8C9B] hover:bg-[#E5EBF0]'
                                        }`}
                                    >
                                        {cat === 'forex' ? 'Forex' : cat === 'real_estate' ? 'Real Estate' : 'Options'}
                                    </button>
                                ))}
                            </div>

                            <div>
                                <label className="block text-xs font-bold text-[#7B8C9B] uppercase mb-1">Details / Intelligence</label>
                                <textarea 
                                    value={betDetail}
                                    onChange={(e) => setBetDetail(e.target.value)}
                                    placeholder="Wade, details on the entry points or risk profile..."
                                    rows={4}
                                    className="w-full px-4 py-3 rounded-xl border border-[#DCE4EA] focus:outline-none focus:border-[#0D5F6B] text-[#1A2832] resize-none"
                                    required
                                />
                            </div>

                            <button 
                                type="submit"
                                disabled={isLogging}
                                className="w-full bg-[#0D5F6B] text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 hover:bg-[#0a4d57] transition disabled:opacity-50"
                            >
                                {isLogging ? 'Logging...' : (
                                    <>
                                        <Plus className="w-5 h-5" />
                                        Push to Priority Board
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </section>

                {/* Section 3: Wealth Stats (Placeholder for Future) */}
                <div className="grid grid-cols-2 gap-4">
                    <div className="bg-white p-4 rounded-2xl border border-[#E5EBF0]">
                        <div className="w-10 h-10 bg-[#D9EEF2] rounded-full flex items-center justify-center mb-3">
                            <DollarSign className="w-5 h-5 text-[#0D5F6B]" />
                        </div>
                        <p className="text-[10px] font-bold text-[#7B8C9B] uppercase">Net Worth Index</p>
                        <p className="text-xl font-bold text-[#1A2832] tracking-tight">$---,---</p>
                    </div>
                    <div className="bg-white p-4 rounded-2xl border border-[#E5EBF0]">
                        <div className="w-10 h-10 bg-[#F5F8FA] rounded-full flex items-center justify-center mb-3">
                            <Target className="w-5 h-5 text-[#7B8C9B]" />
                        </div>
                        <p className="text-[10px] font-bold text-[#7B8C9B] uppercase">Wade Efficiency</p>
                        <p className="text-xl font-bold text-[#1A2832] tracking-tight">98.2%</p>
                    </div>
                </div>
            </div>
        </div>
    );
}
